import {Injectable} from '@angular/core';
import Pusher from 'pusher-js';
import {environment} from '../../environments/environment';


@Injectable({
  providedIn: 'root'
})
export class PusherService {
  private pusher: any;
  private channel: any;
  private requestId: string | null = null;
  private channelName: string = '';

  private eventBindings: Map<string, boolean> = new Map();

  constructor() {
    const pusherKey = environment.pusher.key;
    const pusherOptions = {
      authEndpoint: `${environment.apiUrl}/pusher/auth`.toString(),
      cluster: environment.pusher.cluster,
      activityTimeout: 60000,
    }

    this.pusher = new Pusher(pusherKey, pusherOptions);
  }


  /**
   * Initialize Pusher connection with a specific requestId
   * @param requestId The unique ID for this test session
   */
  public initSocket(requestId: string = ''): void {
    if (this.requestId === requestId && this.channel) {
      console.log(`Already connected to requestId: ${requestId}`);
      return;
    }

    this.requestId = requestId;

    try {
      if (!environment.apiUrl) {
        console.error('Missing API URL in environment configuration');
        return;
      }

      console.log(`Initializing Pusher connection with requestId: ${requestId}`);

      // In Pusher, we'll create a unique channel for this test session
      this.channelName = `test-channel-${requestId}`;

      // Subscribe to the channel
      this.channel = this.pusher.subscribe(this.channelName);

      // Log when connection is established
      this.pusher.connection.bind('connected', () => {
        console.log('Pusher connected');
      });

      // Log connection errors
      this.pusher.connection.bind('error', (err: any) => {
        console.error('Pusher connection error:', err);
      });

    } catch (error) {
      console.error('Error initializing Pusher:', error);
    }
  }


  /**
   * Connect to Pusher and register for test events
   * @param requestId The unique ID for this test session
   */
  public connect(requestId: string = ''): void {
    if (this.requestId === requestId && this.channel) {
      console.log(`Already connected to requestId: ${requestId}`);
      return;
    }

    if (requestId) {
      this.requestId = requestId;
      this.channelName = `test-channel-${requestId}`;

      // If we already have a channel subscription, unbind and unsubscribe
      if (this.channel) {
        this.pusher.unsubscribe(this.channelName);
      }

      // Subscribe to the new channel
      this.channel = this.pusher.subscribe(this.channelName);

      // // Register this client with the backend
      // this.registerForTest();

      console.log('Pusher channel subscribed:', this.channelName);
    } else {
      console.error('RequestId is required for connection');
    }
  }


  /**
   * Clean up existing channel subscription and event bindings
   */
  private cleanupExistingChannel(): void {
    if (this.channel && this.channelName) {
      // Unbind all events before unsubscribing
      this.channel.unbind_all();
      this.pusher.unsubscribe(this.channelName);
      this.channel = null;
      console.log('Previous Pusher channel cleaned up');

      // Clear event bindings tracking
      this.eventBindings.clear();
    }
  }


  /**
   * Disconnect from Pusher
   */
  public disconnect(): void {
    this.cleanupExistingChannel();
  }

  public startTestRun(domain: string, filter:string = 'test-run'): void {
    if (!this.requestId) {
      console.error('RequestId not set');
      return;
    }

    const runData = {
      url: domain,
      requestId: this.requestId,
      testCategory: filter,
      channel: this.channelName
    };

    // Make an HTTP request to start the test
    fetch(`${environment.apiUrl}/start-test-run`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(runData),
      credentials: 'include'
    })
      .then(response => response.json())
      .then(data => console.log('Test started:', data))
      .catch(error => console.error('Error starting test:', error));
  }

  /**
   * Listen for test completion events
   * @param callback Function to call when test completes
   */
  public onTestCompleted(callback: (data: any) => void): void {
    this.bindEventOnce('testCompleted', callback);
  }

  /**
   * Listen for test error events
   * @param callback Function to call when test errors
   */
  public onTestError(callback: (data: any) => void): void {
    this.bindEventOnce('testError', callback);
  }

  /**
   * Listen for progress update events
   * @param callback Function to call when progress updates
   */
  public onProgress(callback: (data: any) => void): void {
    this.bindEventOnce('progressUpdate', callback);
  }

  /**
   * Listen for debug log events
   * @param callback Function to call when debug logs are received
   */
  public onDebugLog(callback: (data: any) => void): void {
    this.bindEventOnce('debugLog', callback);
  }

  /**
   * Bind an event only once to prevent duplicate listeners
   * @param eventName Name of the event to bind
   * @param callback Function to call when event occurs
   */
  private bindEventOnce(eventName: string, callback: (data: any) => void): void {
    if (!this.channel) {
      console.error('Channel not initialized');
      return;
    }

    // If this event is already bound, unbind it first
    if (this.eventBindings.get(eventName)) {
      this.channel.unbind(eventName);
      console.log(`Unbound existing '${eventName}' event`);
    }

    // Bind the event and mark it as bound
    this.channel.bind(eventName, callback);
    this.eventBindings.set(eventName, true);
    console.log(`Bound '${eventName}' event`);
  }


}
